import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  useRouteDocumentTitle,
} from '@client/toolkit';

import {
  BuildingFilterPanel,
  EnergyBadge,
  KpiTile,
  PLACEHOLDER,
  ReportTableSkeletonRows,
  formatNumber,
  formatPercent,
  useBuildingFilter,
} from '../components/portfolio-report';
import { ReportingPaths } from '../reporting.router';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { YearPicker } from '../../../components';
import { useYearParam } from '../../../hooks';
import { PortfolioOverviewBuilding, useGetReportingPortfolioOverviewQuery } from '@client/api';

// Buildings older than this with a poor (D–G) energy label carry transition/capex "stranding" risk.
const STRANDING_YEAR = 1990;
const LABELS_AC = new Set(['A', 'B', 'C']);
const LABELS_DG = new Set(['D', 'E', 'F', 'G']);

type EnergyBand = 'ac' | 'dg';

// Two bands matching the KPI split: efficient (A–C) vs low-efficiency / SFDR PAI 18 (D–G).
function bandOf(label: string | null | undefined): EnergyBand | null {
  if (!label) return null;
  const upper = label.toUpperCase();
  if (LABELS_AC.has(upper)) return 'ac';
  if (LABELS_DG.has(upper)) return 'dg';
  return null;
}

function isStranded(b: PortfolioOverviewBuilding): boolean {
  return (
    b.constructionYear != null &&
    b.constructionYear < STRANDING_YEAR &&
    !!b.energyLabel &&
    LABELS_DG.has(b.energyLabel.toUpperCase())
  );
}

export function ReportingEnergyProfileRoute() {
  const { t } = useTranslation('reporting');
  const { t: tType } = useTranslation('type');
  useRouteDocumentTitle(t('energyProfile.documentTitle'), ReportingPaths.EnergyProfile);

  const { selectedYear, setSelectedYear } = useYearParam();

  // The register is year-agnostic, but the year picker is kept for consistency with the other
  // portfolio reports. We use isFetching (not isLoading) so skeletons also show while refetching.
  const { data, isFetching } = useGetReportingPortfolioOverviewQuery({
    startDate: selectedYear.startOf('year').format('YYYY-MM-DD'),
    endDate: selectedYear.endOf('year').format('YYYY-MM-DD'),
  });

  const buildings = useMemo(() => data?.buildings ?? [], [data]);
  const { buildingFilter, setBuildingFilter, activeTags, toggleTag, allTags, filtered } = useBuildingFilter(buildings);

  const translateType = (type: string | null | undefined): string =>
    type ? tType(`BuildingType.${type}`, { defaultValue: type }) : PLACEHOLDER;

  // KPI derivations. All KPIs follow the active filters (building search + tags) so the four
  // boxes always describe the same set shown in the register below.
  const kpis = useMemo(() => {
    let totalArea = 0;
    let areaWithLabel = 0;
    let areaAC = 0;
    let areaDG = 0;
    let strandedCount = 0;

    filtered.forEach((b) => {
      const area = b.grossAreaM2 || 0;
      totalArea += area;
      const label = b.energyLabel?.toUpperCase();
      if (label) {
        areaWithLabel += area;
        if (LABELS_AC.has(label)) areaAC += area;
        if (LABELS_DG.has(label)) areaDG += area;
      }
      if (isStranded(b)) strandedCount += 1;
    });

    return {
      // Share of labelled area that is efficient (A–C) / inefficient (D–G).
      efficientShare: areaWithLabel > 0 ? (areaAC / areaWithLabel) * 100 : null,
      inefficientShare: areaWithLabel > 0 ? (areaDG / areaWithLabel) * 100 : null,
      // Share of total area covered by a valid energy certificate.
      coverage: totalArea > 0 ? (areaWithLabel / totalArea) * 100 : null,
      strandedCount,
    };
  }, [filtered]);

  const filterPanel = (
    <BuildingFilterPanel
      title={t('energyProfile.filter.title')}
      searchPlaceholder={t('energyProfile.filter.building')}
      noTagsLabel={t('energyProfile.filter.noTags')}
      buildingFilter={buildingFilter}
      onBuildingFilterChange={setBuildingFilter}
      allTags={allTags}
      activeTags={activeTags}
      onToggleTag={toggleTag}
      action={<YearPicker value={selectedYear} onChange={setSelectedYear} />}
    />
  );

  return (
    <ReportPageContent breadcrumb={t('energyProfile.breadcrumb')} menu={filterPanel}>
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <KpiTile
          label={t('energyProfile.kpi.efficient')}
          sub={t('energyProfile.kpi.efficientSub')}
          value={formatPercent(kpis.efficientShare)}
          loading={isFetching}
        />
        <KpiTile
          label={t('energyProfile.kpi.inefficient')}
          sub={t('energyProfile.kpi.inefficientSub')}
          value={formatPercent(kpis.inefficientShare)}
          loading={isFetching}
        />
        <KpiTile
          label={t('energyProfile.kpi.coverage')}
          sub={t('energyProfile.kpi.coverageSub')}
          value={formatPercent(kpis.coverage)}
          loading={isFetching}
        />
        <KpiTile
          label={t('energyProfile.kpi.stranding')}
          sub={t('energyProfile.kpi.strandingSub')}
          value={formatNumber(kpis.strandedCount)}
          loading={isFetching}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle variant="highlighted">{t('energyProfile.register.title')}</CardTitle>
        </CardHeader>
        <CardContent variant="fill" className="px-4 pb-4">
          <Table className="w-full" size="small">
            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{t('energyProfile.register.header.building')}</TableHeadCell>
                <TableHeadCell>{t('energyProfile.register.header.type')}</TableHeadCell>
                <TableHeadCell align="right">{t('energyProfile.register.header.area')}</TableHeadCell>
                <TableHeadCell align="right">{t('energyProfile.register.header.year')}</TableHeadCell>
                <TableHeadCell>{t('energyProfile.register.header.energy')}</TableHeadCell>
                <TableHeadCell>{t('energyProfile.register.header.band')}</TableHeadCell>
                <TableHeadCell>{t('energyProfile.register.header.certificate')}</TableHeadCell>
                <TableHeadCell>{t('energyProfile.register.header.stranding')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                <ReportTableSkeletonRows columns={8} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <span className="text-white/60">{t('energyProfile.register.empty')}</span>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((b) => {
                  const band = bandOf(b.energyLabel);
                  const stranded = isStranded(b);
                  return (
                    <TableRow key={b.id}>
                      <TableCell>
                        <Link
                          to={`/portfolio/buildings/${b.id}`}
                          className="font-medium text-white hover:underline focus:outline-none focus:underline"
                        >
                          {b.name}
                        </Link>
                      </TableCell>
                      <TableCell>{translateType(b.type)}</TableCell>
                      <TableCell align="right">{formatNumber(b.grossAreaM2)}</TableCell>
                      <TableCell align="right">{b.constructionYear ?? PLACEHOLDER}</TableCell>
                      <TableCell>
                        <EnergyBadge label={b.energyLabel ?? null} />
                      </TableCell>
                      <TableCell>{band ? t(`energyProfile.band.${band}`) : PLACEHOLDER}</TableCell>
                      <TableCell>
                        {b.energyCertificateUrl ? (
                          <a
                            href={b.energyCertificateUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-primary hover:underline focus:outline-none focus:underline"
                          >
                            {t('energyProfile.register.certificateLink')}
                          </a>
                        ) : (
                          <span className="text-white/40">{PLACEHOLDER}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {stranded ? (
                          <span className="text-rose-300">{t('energyProfile.register.yes')}</span>
                        ) : (
                          <span className="text-white/60">{t('energyProfile.register.no')}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </ReportPageContent>
  );
}
